import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ProviderDetail } from '../../../../../js/api';
import { formatEin } from '../../../../../js/utils/string';
import { CopyableText } from '../../../components/copyable-text';
import { DocumentTypeAccess } from '../../../components/document-type-access';
import { EmailContact } from '../../../components/email-contact';
import { TooltippedText } from '../../../components/tooltipped-text';
import { WorkflowScopes } from '../../../constants/workflows';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';
import { orgProviderProfilePath } from '../../../utils/navigation';
import {
  EMPTY_VALUE,
  formatProviderDate,
  labelFor,
  labelsFor,
  ProviderDrawerMaps,
  textOrDash,
  yesNo,
} from './providerDisplay';
import { FieldSection, FieldSpec } from './ProviderDrawerPrimitives';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY } from './queryKeys';

/** "Ada (Addie)" when the preferred name differs from the first name, else just the first name. */
const formatFirstName = (firstName: string, preferredName: string): string => {
  const first = textOrDash(firstName);
  const preferred = preferredName.trim();
  return preferred && preferred !== firstName.trim() ? `${first} (${preferred})` : first;
};

/** DocumentType slug for the provider's CV (uploaded on the Documents tab). */
const CV_TYPE_SLUG = 'curriculum-vitae';

/** Basic info (incl. professional links), contact, address, birth location and military. */
export function GeneralInfoTab({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // the legacy org profile page is entity-scoped; staff pick an entity first
  const requireActingAsEntity = useRequireActingAsEntity();
  // The CV belongs beside the clinician's basics: every clinician needs one on file to
  // get connected, so it is reachable here without opening the Documents tab. Name is
  // a one-click view; the paperclip menu holds the other actions the viewer may take.
  const cvValue = (
    <DocumentTypeAccess
      mode="latest"
      contextType={WorkflowScopes.Provider}
      contextId={provider.id}
      ownerId={provider.id}
      documentTypeSlug={CV_TYPE_SLUG}
      // no CV in the documents framework yet: the legacy flag, linking to where the
      // legacy file can still be downloaded (org profile → Professional Traits)
      emptyContent={
        provider.has_cv ? (
          <TooltippedText
            tooltipContent={
              <span className="tw:flex tw:flex-col tw:items-start">
                <span>Open the legacy profile&apos;s Professional Traits</span>
                <span>to download CV</span>
              </span>
            }
          >
            <button
              type="button"
              className="tw:text-brand-green-0 tw:hover:underline"
              onClick={() =>
                requireActingAsEntity(() =>
                  navigate(`${orgProviderProfilePath(provider.id)}/professional-traits`)
                )
              }
              data-testid="provider-legacy-cv-link"
            >
              Yes
            </button>
          </TooltippedText>
        ) : (
          yesNo(false)
        )
      }
      onDocumentsChanged={() =>
        queryClient.invalidateQueries({
          queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, provider.id],
        })
      }
      data-testid="provider-cv"
    />
  );

  // identifiers / contact numbers get a copy icon (see .mex/SWEEPS.md)
  const copyable = (value: string | null | undefined) =>
    value ? <CopyableText value={value} /> : EMPTY_VALUE;

  const basicFields: FieldSpec[] = [
    { label: 'Prefix', value: textOrDash(provider.prefix_name) },
    { label: 'First Name', value: formatFirstName(provider.first_name, provider.preferred_name) },
    { label: 'Middle Name', value: textOrDash(provider.middle_name) },
    { label: 'Last Name', value: textOrDash(provider.last_name) },
    { label: 'Credential', value: textOrDash(provider.credential_name) },
    { label: 'Other Names', value: textOrDash(provider.other_names) },
    { label: 'Degree(s)', value: labelsFor(provider.degree, maps.Degree) },
    { label: 'NPI', value: copyable(provider.npi) },
    { label: 'Birthday', value: formatProviderDate(provider.birth_date) },
    { label: 'Gender', value: labelFor(provider.gender, maps.Gender) },
    { label: 'Languages Spoken', value: labelsFor(provider.languages_spoken, maps.Language) },
    { label: 'EIN', value: provider.ein ? formatEin(provider.ein) : EMPTY_VALUE },
    { label: 'Business Name', value: textOrDash(provider.business_name) },
    {
      label: 'LinkedIn',
      value: provider.linkedin_link ? (
        <a
          href={provider.linkedin_link}
          target="_blank"
          rel="noopener noreferrer"
          className="tw:text-brand-green-0 tw:hover:underline tw:break-all"
        >
          {provider.linkedin_link}
        </a>
      ) : (
        EMPTY_VALUE
      ),
    },
    { label: 'CV', value: cvValue },
  ];

  const contactFields: FieldSpec[] = [
    {
      label: 'Email',
      value: provider.email ? <EmailContact email={provider.email} /> : EMPTY_VALUE,
    },
    { label: 'Phone', value: copyable(provider.phone_number) },
    { label: 'Alt Phone', value: copyable(provider.phone_number_alternative) },
    {
      label: 'Texting',
      value: provider.texting_number ? copyable(provider.texting_number) : 'Opted out',
    },
  ];

  const addressFields: FieldSpec[] = [
    {
      label: 'Street',
      value: provider.street_address2 ? (
        <span className="tw:flex tw:flex-col">
          <span>{textOrDash(provider.street_address)}</span>
          <span>{provider.street_address2}</span>
        </span>
      ) : (
        textOrDash(provider.street_address)
      ),
    },
    {
      label: 'City, State',
      value: `${textOrDash(provider.city)}, ${textOrDash(provider.state)}`,
    },
    { label: 'Zip Code', value: textOrDash(provider.zip_code) },
  ];

  const birthFields: FieldSpec[] = [
    { label: 'City', value: textOrDash(provider.birth_city) },
    { label: 'State', value: textOrDash(provider.birth_state) },
    { label: 'Country', value: textOrDash(provider.birth_country) },
  ];

  const militaryFields: FieldSpec[] = [
    { label: 'Status', value: labelFor(provider.military_status, maps.MilitaryStatus) },
    { label: 'Branch', value: labelFor(provider.military_branch, maps.MilitaryBranch) },
    { label: 'DD214 on file', value: yesNo(provider.has_dd214) },
  ];

  return (
    <div className="tw:grid tw:grid-cols-1 tw:@xl:grid-cols-2 tw:gap-x-3 tw:gap-y-4">
      <FieldSection title="Basic Info" fields={basicFields} data-testid="provider-basic-info" />
      <div className="tw:flex tw:flex-col tw:gap-4">
        <FieldSection title="Contact Info" fields={contactFields} />
        <FieldSection title="Address" fields={addressFields} />
        <FieldSection title="Birth Location" fields={birthFields} />
        <FieldSection title="Military" fields={militaryFields} />
      </div>
    </div>
  );
}
